<!-- CompanyInfo.vue (Código Completo com Validações Atualizadas) -->
<script setup>
import { ref, reactive, watch, computed, onMounted, onBeforeUnmount, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useRoute } from 'vue-router';
import Input from '../../../../../components-next/input/Input.vue';
import Textarea from '../../../../../components-next/textarea/TextArea.vue';
import Button from '../../../../../components-next/button/Button.vue';
import PhoneInput from './PhoneInput.vue';
import LocationInput from './LocationInput.vue';
import DynamicLocationList from './DynamicLocationList.vue';
import WebsiteInput from './WebsiteInput.vue';
import EmailInput from './EmailInput.vue';
import DynamicFieldList from './DynamicFieldList.vue';
import ValidationFeedback from '../shared/ValidationFeedback.vue';

const { t } = useI18n();
const route = useRoute();
const emit = defineEmits(['update', 'validation']);

// Verificar se estamos no modo de edição
const isEditMode = computed(() => route.query.mode === 'edit');

const props = defineProps({
  initialCompany: {
    type: Object,
    default: () => ({
      name: '',
      website: '',
      description: '',
      locations: [],
      support: {
        phone: '',
        email: '',
        faqLink: ''
      },
      policies: []
    })
  },
  validationErrors: {
    type: Object,
    default: () => ({
      name: '',
      website: '',
      description: '',
      support: {
        email: '',
        phone: ''
      }
    })
  }
});

const selectedLocation = ref('');
const hasShowedDescriptionAlert = ref(false);

// Flags para controlar a animação dos botões
const isDescriptionButtonHovered = ref(false);
const isPoliciesButtonHovered = ref(false);
const isLocationsButtonHovered = ref(false);

// Função para criar uma cópia profunda do objeto (otimizada)
function deepClone(obj) {
  if (obj === null || typeof obj !== 'object') return obj;
  if (obj instanceof Date) return new Date(obj);
  if (Array.isArray(obj)) return obj.map(item => deepClone(item));
  
  const cloned = {};
  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      cloned[key] = deepClone(obj[key]);
    }
  }
  return cloned;
}

// Inicializar companyInfo com uma cópia completa dos dados iniciais
const companyInfo = reactive(deepClone({
  name: '',
  cnpj: '',
  website: '',
  description: '',
  locations: [],
  support: {
    phone: '',
    email: '',
    faqLink: ''
  },
  policies: []
}));

// Função para atualizar companyInfo com dados de initialCompany
function updateCompanyInfo() {
  
  isUpdatingCompany.value = true;
  
  const initial = props.initialCompany;
  
  // Atualizar campos de primeiro nível
  companyInfo.name = initial.name || '';
  companyInfo.cnpj = initial.cnpj || '';
  companyInfo.website = initial.website || '';
  // Garantir que description seja sempre uma string
  companyInfo.description = (initial.description !== null && initial.description !== undefined) ? String(initial.description) : '';
  companyInfo.locations = Array.isArray(initial.locations) ? deepClone(initial.locations) : [];
  
  // Atualizar campos de suporte
  if (initial.support) {
    companyInfo.support.phone = initial.support.phone || '';
    companyInfo.support.email = initial.support.email || '';
    companyInfo.support.faqLink = initial.support.faqLink || '';
  }
  
  // Atualizar políticas
  companyInfo.policies = Array.isArray(initial.policies) 
    ? deepClone(initial.policies) 
    : [];
  
  // Usar setTimeout para quebrar o ciclo reativo
  setTimeout(() => {
    isUpdatingCompany.value = false;
  }, 0);
}

// Inicializar companyInfo com os dados do props no mounted
onMounted(async () => {
  
  updateCompanyInfo();
  
  // No modo edição, marcar todos os campos como tocados se já têm valor
  if (isEditMode.value) {
    touchedFields.name = !!companyInfo.name;
    touchedFields.cnpj = !!companyInfo.cnpj;
    touchedFields.website = !!companyInfo.website;
    touchedFields.description = !!companyInfo.description;
    touchedFields.email = !!companyInfo.support.email;
  }
  
  // Validar todos os campos
  await nextTick();
  validateAllFields();
  
  // Garantir que o estado de validação seja emitido após a montagem
  // Isso é crucial para o modo edição
  await nextTick();
  emit('validation', isFormValid.value);
});

// Status de validação para cada campo
const validationStatus = reactive({
  name: false,
  cnpj: true, // CNPJ é opcional, então começa como true
  website: true, // Website agora é opcional, então começa como true
  description: false, 
  email: true, // Email agora é opcional, então começa como true
  phone: true, // Telefone é opcional, então começa como true
  faqLink: true, // FAQ link é opcional, então começa como true
  locations: true, // Localizações são opcionais, então começa como true
  policies: true // Políticas começam como válidas (não há nenhuma)
});

// Controle de campos tocados para validação
// No modo edição, considera todos os campos como já tocados
const touchedFields = reactive({
  name: isEditMode.value,
  cnpj: isEditMode.value,
  website: isEditMode.value,
  description: isEditMode.value,
  email: isEditMode.value
});

// Erros internos
const internalErrors = reactive({
  name: '',
  cnpj: '',
  website: '',
  description: '',
  email: ''
});

// Computed para verificar se o formulário inteiro é válido
const isFormValid = computed(() => {
  return validationStatus.name && 
         validationStatus.cnpj &&
         validationStatus.website && 
         validationStatus.description && 
         validationStatus.email &&
         validationStatus.phone &&
         validationStatus.faqLink &&
         validationStatus.locations &&
         validationStatus.policies;
});

// Flags para evitar atualizações recursivas
const isUpdatingCompany = ref(false);
const isValidating = ref(false);
const isTypingDescription = ref(false);

// Debounce timer for validation
let validationTimer = null;

// Observa mudanças e emite atualizações para o componente pai (com debounce)
watch(companyInfo, () => {
  if (isUpdatingCompany.value || isValidating.value) return; // Adiciona proteção contra loop
  
  // Debounce updates to prevent excessive emissions
  clearTimeout(validationTimer);
  validationTimer = setTimeout(() => {
    if (!isUpdatingCompany.value && !isValidating.value) { // Dupla verificação
      emit('update', deepClone(companyInfo));
    }
  }, 200); // Aumenta debounce para 200ms
}, { deep: true });


// Quando os status de validação mudam, emite evento de validação
watch(validationStatus, () => {
  if (isValidating.value) return; // Evita atualizações recursivas
  
  isValidating.value = true;
  emit('validation', isFormValid.value);
  // Usa setTimeout para quebrar o ciclo de atualização síncrona
  setTimeout(() => {
    isValidating.value = false;
  }, 0);
}, { deep: true });

// Watch para props.initialCompany
watch(() => props.initialCompany, (newVal, oldVal) => {
  
  // Prevent updates if user is actively typing
  if (isTypingDescription.value || isUpdatingCompany.value) {
    return;
  }
  
  // Prevent unnecessary updates if values haven't actually changed
  if (JSON.stringify(newVal) === JSON.stringify(oldVal)) {
    return;
  }
  
  // Check if we have meaningful data in newVal (not just empty strings)
  const hasData = newVal && (newVal.name || newVal.website || newVal.description || newVal.support?.email);
  
  // Only update if we're not currently editing and there's meaningful data
  if (hasData && !touchedFields.description) {
    isUpdatingCompany.value = true;
    nextTick(() => {
      updateCompanyInfo();
      nextTick(() => {
        validateAllFields();
      });
    });
    return;
  }
  
  // For empty/cleared data, only update if no fields have been touched
  const noFieldsTouched = !touchedFields.name && !touchedFields.description && 
                         !touchedFields.website && !touchedFields.email && 
                         !touchedFields.cnpj;
  
  if (noFieldsTouched) {
    isUpdatingCompany.value = true;
    nextTick(() => {
      updateCompanyInfo();
      nextTick(() => {
        validateAllFields();
      });
    });
  }
}, { deep: true });

// Função para exibir alerta de descrição
const showDescriptionAlert = () => {
  if (!hasShowedDescriptionAlert.value) {
    useAlert('Dica: Quanto mais detalhada for a descrição da sua empresa, melhor será o desempenho do seu agente de atendimento ao responder perguntas sobre seus produtos e serviços.', { type: 'info', duration: 6000 });
    hasShowedDescriptionAlert.value = true;
  }
};

// Handlers para os cliques nos botões de informação
const handleDescriptionInfoClick = () => {
  isDescriptionButtonHovered.value = true;
  useAlert('Descreva detalhadamente as atividades, produtos e serviços da sua empresa. Isso ajudará o agente a compreender o contexto e fornecer respostas mais precisas aos seus clientes.', { type: 'info', duration: 7000 });
};

const handlePoliciesInfoClick = () => {
  isPoliciesButtonHovered.value = true;
  useAlert('Configure as políticas da empresa que o agente deve seguir, como termos de uso, garantias, política de troca e devolução, etc.', { type: 'info', duration: 7000 });
};

const handleLocationsInfoClick = () => {
  isLocationsButtonHovered.value = true;
  useAlert('Adicione os endereços físicos da sua empresa, como matriz, filiais, pontos de atendimento, etc. Isso ajudará o agente a fornecer informações precisas de localização aos clientes.', { type: 'info', duration: 7000 });
};

// Função para adicionar localização
const addLocation = () => {
  companyInfo.locations.unshift({
    street: '',
    number: '',
    complement: '',
    neighborhood: '',
    city: '',
    state: '',
    zipCode: '',
    streetError: '',
    numberError: '',
    complementError: '',
    neighborhoodError: '',
    cityError: '',
    stateError: '',
    zipCodeError: ''
  });
  
  // Quando adiciona uma nova localização, marca que há localizações que precisam ser validadas
  validationStatus.locations = false;
  
  // Emite o estado de validação atualizado
  emit('validation', isFormValid.value);
};

// Função para atualizar localizações
const updateLocations = (locations) => {
  companyInfo.locations = locations;
  
  // Se não houver localizações, considera válido
  if (!locations || locations.length === 0) {
    validationStatus.locations = true;
  }
  
  // Emite o estado de validação atualizado
  emit('validation', isFormValid.value);
};

// Atualização da função para atualizar políticas
const updatePolicies = (policies) => {
  companyInfo.policies = policies;
  
  // Se não houver políticas, considera válido
  if (!policies || policies.length === 0) {
    validationStatus.policies = true;
  }
  
  // Emite o estado de validação atualizado
  emit('validation', isFormValid.value);
};

// Atualizar o clique no botão de adicionar política
const addPolicy = () => {
  companyInfo.policies.unshift({
    name: '',
    value: '',
    nameError: '',
    valueError: ''
  });
  
  // Quando adiciona uma nova política, marca que há políticas que precisam ser validadas
  validationStatus.policies = false;
  
  // Emite o estado de validação atualizado
  emit('validation', isFormValid.value);
};

// — Regex para validações de nome e descrição —
const companyNameRegex = /^[\p{L}\p{N} ._-]+$/u;

// Validação de nome da empresa
const validateCompanyName = (name, updateTouched = true) => {
  if (updateTouched) {
    touchedFields.name = true;
  }
  
  if (!name || !name.trim()) {
    internalErrors.name = 'O nome da empresa é obrigatório';
    validationStatus.name = false;
    return false;
  }
  
  if (name.length < 3 || name.length > 60) {
    internalErrors.name = 'O nome deve ter entre 3 e 60 caracteres';
    validationStatus.name = false;
    return false;
  }
  
  // Só permite letras (com acento), números, espaço, ponto, underline, hífen
  if (!companyNameRegex.test(name)) {
    internalErrors.name = 'Use apenas letras (com ou sem acento), números, espaço, ponto (.), hífen (-) ou sublinhado (_)';
    validationStatus.name = false;
    return false;
  }
  
  internalErrors.name = '';
  validationStatus.name = true;
  return true;
};

// Validação de descrição da empresa
const validateCompanyDescription = (description, updateTouched = true) => {
  if (updateTouched) {
    touchedFields.description = true;
  }
  
  if (!description || !description.trim()) {
    internalErrors.description = 'A descrição da empresa é obrigatória';
    validationStatus.description = false;
    return false;
  }
  
  if (description.length < 250 || description.length > 2000) {
    internalErrors.description = 'A descrição deve ter entre 250 e 2000 caracteres';
    validationStatus.description = false;
    return false;
  }
  
  // Verifica apenas caracteres não permitidos (< e >)
  if (description.includes('<') || description.includes('>')) {
    internalErrors.description = 'Os caracteres < e > não são permitidos';
    validationStatus.description = false;
    return false;
  }
  
  internalErrors.description = '';
  validationStatus.description = true;
  return true;
};


// Validação de e-mail
const validateEmail = (email, updateTouched = true) => {
  if (updateTouched) {
    touchedFields.email = true;
  }
  
  // Email agora é opcional
  if (!email || !email.trim()) {
    internalErrors.email = '';
    validationStatus.email = true;
    return true;
  }
  
  // Regex para verificar formato de email
  const re = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  if (!re.test(String(email).toLowerCase())) {
    internalErrors.email = 'Por favor, insira um email válido';
    validationStatus.email = false;
    return false;
  }
  
  internalErrors.email = '';
  validationStatus.email = true;
  return true;
};

// Função auxiliar para validar os campos dinâmicos
const validatePolicies = () => {
  const hasEmptyPolicies = companyInfo.policies.some(
    policy => !policy.name || !policy.name.trim() || !policy.value || !policy.value.trim()
  );
  validationStatus.policies = !hasEmptyPolicies;
  return !hasEmptyPolicies;
};

// Validação de CNPJ
const validateCNPJ = (cnpj, updateTouched = true) => {
  if (updateTouched) {
    touchedFields.cnpj = true;
  }
  
  // CNPJ é opcional
  if (!cnpj || !cnpj.trim()) {
    internalErrors.cnpj = '';
    validationStatus.cnpj = true;
    return true;
  }
  
  // Remove caracteres não numéricos
  const cnpjNumerico = cnpj.replace(/\D/g, '');
  
  if (cnpjNumerico.length !== 14) {
    internalErrors.cnpj = 'CNPJ deve ter 14 dígitos';
    validationStatus.cnpj = false;
    return false;
  }
  
  // Validação básica de CNPJ
  if (/^(\d)\1{13}$/.test(cnpjNumerico)) {
    internalErrors.cnpj = 'CNPJ inválido';
    validationStatus.cnpj = false;
    return false;
  }
  
  internalErrors.cnpj = '';
  validationStatus.cnpj = true;
  return true;
};

// Função para formatar CNPJ
const formatCNPJ = (value) => {
  const cnpj = value.replace(/\D/g, '');
  if (cnpj.length <= 2) return cnpj;
  if (cnpj.length <= 5) return cnpj.slice(0, 2) + '.' + cnpj.slice(2);
  if (cnpj.length <= 8) return cnpj.slice(0, 2) + '.' + cnpj.slice(2, 5) + '.' + cnpj.slice(5);
  if (cnpj.length <= 12) return cnpj.slice(0, 2) + '.' + cnpj.slice(2, 5) + '.' + cnpj.slice(5, 8) + '/' + cnpj.slice(8);
  return cnpj.slice(0, 2) + '.' + cnpj.slice(2, 5) + '.' + cnpj.slice(5, 8) + '/' + cnpj.slice(8, 12) + '-' + cnpj.slice(12, 14);
};

// Handler para input de CNPJ
const handleCNPJInput = (event) => {
  const formatted = formatCNPJ(event.target.value);
  companyInfo.cnpj = formatted;
  if (touchedFields.cnpj) {
    validateCNPJ(formatted);
  }
};

// Função para validar todos os campos
const validateAllFields = () => {
  isValidating.value = true;
  
  // Sempre executar validações completas, independente do modo
  validateCompanyName(companyInfo.name, false);
  validateCNPJ(companyInfo.cnpj, false);
  validateCompanyDescription(companyInfo.description, false);
  validateEmail(companyInfo.support.email, false);
  
  if (companyInfo.policies.length > 0) {
    validatePolicies();
  }
  
  // REMOVIDO: Lógica especial para modo edição que sobrescreve validações
  // As validações detalhadas já definiram os status corretos
  
  setTimeout(() => {
    emit('validation', isFormValid.value);
    isValidating.value = false;
  }, 0);
};

// Handlers para os eventos de validação dos componentes filhos
const handleWebsiteValidation = (isValid) => {
  validationStatus.website = isValid;
};

const handlePhoneValidation = (isValid) => {
  validationStatus.phone = isValid;
};

const handleFaqLinkValidation = (isValid) => {
  validationStatus.faqLink = isValid;
};

const handlePoliciesValidation = (isValid) => {
  validationStatus.policies = isValid;
  if (companyInfo.policies.length === 0) {
    validationStatus.policies = true;
  }
};

const handleLocationsValidation = (isValid) => {
  validationStatus.locations = isValid;
  if (companyInfo.locations.length === 0) {
    validationStatus.locations = true;
  }
};


// Computed para mensagens de erro
const nameErrorMessage = computed(() => {
  if (!touchedFields.name) return '';
  return props.validationErrors.name || internalErrors.name;
});

const cnpjErrorMessage = computed(() => {
  if (!touchedFields.cnpj) return '';
  return props.validationErrors.cnpj || internalErrors.cnpj;
});

const descriptionErrorMessage = computed(() => {
  if (!touchedFields.description) return '';
  return props.validationErrors.description || internalErrors.description;
});

const emailErrorMessage = computed(() => {
  if (!touchedFields.email) return '';
  return props.validationErrors.support?.email || internalErrors.email;
});

// Hardcoded placeholder para evitar parsing do '@' no i18n
const emailPlaceholder = computed(() => 'exemple.email@domain.com');

// Handler para o input da descrição
function handleDescriptionInput() {
  isTypingDescription.value = true;
  validateCompanyDescription(companyInfo.description);
  
  // Emite atualização durante a digitação com debounce maior
  clearTimeout(validationTimer);
  validationTimer = setTimeout(() => {
    if (!isUpdatingCompany.value) {
      emit('update', deepClone(companyInfo));
    }
  }, 300); // 300ms debounce para digitação
}

// Handler para o blur da descrição
function handleDescriptionBlur() {
  touchedFields.description = true;
  validateCompanyDescription(companyInfo.description);
  isTypingDescription.value = false;
  
  // Força a emissão imediata do update após terminar de digitar
  clearTimeout(validationTimer);
  setTimeout(() => {
    if (!isUpdatingCompany.value) {
      emit('update', deepClone(companyInfo));
    }
  }, 50); // Reduz para 50ms no blur
}

// Cleanup timers before component unmount
onBeforeUnmount(() => {
  if (validationTimer) {
    clearTimeout(validationTimer);
  }
});
</script>

<template>
  <div class="space-y-6">
    <!-- Informações Básicas da Empresa -->
    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
      <!-- Nome da Empresa -->
      <div class="flex flex-col gap-1.5">
        <div class="flex items-center justify-between">
          <label class="text-sm font-medium text-n-slate-12">
            Nome da Empresa *
          </label>
          <span v-if="companyInfo.name" class="text-xs text-n-slate-10">{{ companyInfo.name.length }}/60</span>
        </div>
        <Input
          v-model="companyInfo.name"
          placeholder="Digite o nome da empresa"
          required
          :error="nameErrorMessage"
          :maxlength="60"
          @blur="touchedFields.name = true"
          @input="validateCompanyName(companyInfo.name)"
        />
        <ValidationFeedback 
          :error="nameErrorMessage"
          :touched="touchedFields.name"
          :helper-text="'Use entre 3 e 60 caracteres (letras, números, espaço, ., _ ou -)'"
          :show-char-count="true"
          :current-length="companyInfo.name.length"
          :max-length="60"
          :min-length="3"
        />
      </div>
      <!-- CNPJ -->
      <div class="flex flex-col gap-1.5">
        <div class="flex items-center justify-between">
          <label class="text-sm font-medium text-n-slate-12">
            CNPJ
          </label>
          <span v-if="companyInfo.cnpj" class="text-xs text-n-slate-10">{{ companyInfo.cnpj.replace(/\D/g, '').length }}/14</span>
        </div>
        <Input
          v-model="companyInfo.cnpj"
          placeholder="00.000.000/0000-00"
          :error="cnpjErrorMessage"
          :maxlength="18"
          @input="handleCNPJInput"
          @blur="() => { touchedFields.cnpj = true; validateCNPJ(companyInfo.cnpj); }"
        />
        <ValidationFeedback 
          :error="cnpjErrorMessage"
          :touched="touchedFields.cnpj"
          :helper-text="'Campo opcional - Formato: 00.000.000/0000-00'"
          :show-char-count="false"
        />
      </div>
      <!-- Site -->
      <div class="flex flex-col gap-1.5">
        <label class="text-sm font-medium text-n-slate-12">
          Site
        </label>
        <WebsiteInput
          v-model="companyInfo.website"
          placeholder="Digite o site da sua empresa (opcional)"
          :error="validationErrors.website"
          @validation="handleWebsiteValidation"
        />
      </div>
      <!-- Descrição da Empresa -->
      <div class="flex flex-col gap-1.5 md:col-span-2">
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-2">
            <label class="text-sm font-medium text-n-slate-12">
              Descrição da Empresa *
            </label>
            <button 
              class="inline-flex items-center justify-center w-5 h-5 p-0 text-xs transition-all duration-200 border-0 rounded-full outline-none text-n-slate-11 info-btn"
              :class="{'pulse-animation': !isDescriptionButtonHovered}"
              @mouseenter="isDescriptionButtonHovered = true"
              @click="handleDescriptionInfoClick"
              v-tooltip.top="'Descreva de forma breve e institucional a história da sua empresa, sua área de atuação, principais serviços ou produtos, diferenciais no mercado e o que valoriza na relação com clientes e equipe.'"
            >
              <span class="i-lucide-info"></span>
            </button>
          </div>
          <span v-if="companyInfo.description" class="text-xs text-n-slate-10">{{ companyInfo.description.length }}/2000</span>
        </div>
        <Textarea
          v-model="companyInfo.description"
          placeholder="Descreva as principais atividades da sua empresa, produtos ou serviços oferecidos"
          required
          @blur="handleDescriptionBlur"
          @input="handleDescriptionInput"
          :maxlength="2000"
          :autoHeight="true"
          :resize="true"
          :minHeight="'5rem'"
          :maxHeight="'15rem'"
          :showCharacterCount="false"
          @focus="showDescriptionAlert"
        />
        <ValidationFeedback 
          :error="descriptionErrorMessage"
          :touched="touchedFields.description"
          :helper-text="'Use entre 250-2000 caracteres.'"
          :show-char-count="true"
          :current-length="companyInfo.description.length"
          :max-length="2000"
          :min-length="250"
        />
      </div>
    </div>
    
    <!-- Localizações -->
    <div class="pt-2">
      <div class="flex items-center justify-between mb-2">
        <div class="flex items-center gap-2">
          <h3 class="text-base font-medium text-n-slate-12 flex items-center">
            <span class="i-lucide-map-pin mr-2"></span>
            Localizações
          </h3>
          <button 
            class="inline-flex items-center justify-center w-5 h-5 p-0 text-xs transition-all duration-200 border-0 rounded-full outline-none text-n-slate-11 info-btn"
            :class="{'pulse-animation': !isLocationsButtonHovered}"
            @mouseenter="isLocationsButtonHovered = true"
            @click="handleLocationsInfoClick"
            v-tooltip.top="'Adicione os endereços físicos da sua empresa, como matriz, filiais, pontos de atendimento, etc.'"
          >
            <span class="i-lucide-info"></span>
          </button>
        </div>
        <Button
          label="Adicionar Localização"
          icon="i-lucide-plus"
          variant="outline"
          size="sm"
          @click="addLocation"
        />
      </div>
      <div v-if="companyInfo.locations.length > 0" class="space-y-3">
        <DynamicLocationList
          :items="companyInfo.locations"
          @update="updateLocations"
          @validation="handleLocationsValidation"
        />
      </div>
      <div v-else class="text-sm text-n-slate-11 italic">
        Nenhuma localização foi adicionada. Clique no botão acima para adicionar.
      </div>
    </div>
    
    <!-- Informações de Suporte -->
    <div class="pt-2">
      <h3 class="text-base font-medium mb-3 text-n-slate-12 flex items-center">
        <span class="i-lucide-headset mr-2"></span>
        Informações de Suporte
      </h3>
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <!-- Telefone -->
        <div class="flex flex-col gap-1.5">
          <label class="text-sm font-medium text-n-slate-12">
            Telefone de Suporte
          </label>
          <PhoneInput
            v-model="companyInfo.support.phone"
            placeholder="Digite o número de telefone do suporte"
            :error="validationErrors.support?.phone"
            @validation="handlePhoneValidation"
          />
        </div>
        <!-- Email -->
        <div class="flex flex-col gap-1.5">
          <label class="text-sm font-medium text-n-slate-12">
            Email de Suporte
          </label>
          <EmailInput
            v-model="companyInfo.support.email"
            :placeholder="emailPlaceholder"
            :error="emailErrorMessage"
            @validation="(isValid) => { validationStatus.email = isValid; }"
            @update:modelValue="(value) => { companyInfo.support.email = value; touchedFields.email = true; validateEmail(value); }"
          />
        </div>
        <!-- FAQ Link -->
        <div class="flex flex-col gap-1.5">
          <label class="text-sm font-medium text-n-slate-12">
            Link para FAQ
          </label>
          <WebsiteInput
            v-model="companyInfo.support.faqLink"
            placeholder="Digite o link para a página de FAQ"
            @validation="handleFaqLinkValidation"
          />
        </div>
      </div>
    </div>
    
    <!-- Políticas da Empresa -->
    <div class="space-y-4">
      <!-- Cabeçalho com título e botões -->
      <div class="flex items-center justify-between">
        <h3 class="text-base font-medium text-n-slate-12 flex items-center">
          <span class="i-lucide-shield-check mr-2"></span>
          Políticas da Empresa
          <!-- Botão de informação -->
          <button
            @click="handlePoliciesInfoClick"
            class="ml-2 p-1 rounded-full hover:bg-n-slate-3 transition-colors"
            title="Informações sobre políticas da empresa"
          >
            <svg class="w-4 h-4 text-n-slate-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
          </button>
        </h3>
        <Button
          label="Adicionar Política"
          color="blue"
          variant="outline"
          icon="plus"
          size="sm"
          @click="addPolicy"
        />
      </div>

      <!-- Lista de políticas -->
      <DynamicFieldList
        v-if="companyInfo.policies.length > 0"
        :items="companyInfo.policies"
        name-label="Nome da Política"
        name-placeholder="Ex.: Política de Devolução, Política de Privacidade"
        value-label="Conteúdo da Política"
        value-placeholder="Digite os detalhes da política"
        item-type="policy"
        is-multiline
        @update="updatePolicies"
        @validation="handlePoliciesValidation"
      />

      <!-- Mensagem quando não há políticas -->
      <div v-else class="text-sm text-n-slate-11 italic bg-n-slate-2 dark:bg-n-slate-12/5 rounded-lg p-4 border border-n-slate-5 dark:border-n-slate-10/20">
        <div class="flex items-center gap-2">
          <span class="i-lucide-info w-4 h-4 text-n-slate-9"></span>
          <span>Nenhuma política definida. Clique em "Adicionar Política" para incluir políticas como termos de uso, privacidade ou garantias.</span>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Animação de pulso para os botões de informação */
@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(246, 148, 157, 0.7);
    transform: scale(1);
  }
  50% {
    box-shadow: 0 0 2px 5px rgba(246, 148, 157, 0);
    transform: scale(1.1);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(246, 148, 157, 0);
    transform: scale(1);
  }
}

.pulse-animation {
  animation: pulse 2s infinite;
}

.info-btn {
  width: 20px !important;
  height: 20px !important;
  min-width: 20px !important;
  line-height: 1 !important;
}

</style>